<template>
	<div class="quiz-simple" v-if="game">
		<div class="quiz-simple-content" v-if="currentQuestion">
			<div class="quiz-simple-question text-left mb-8">
				<p class="font-main-medium text-xl" v-if="currentQuestion.text">
					{{currentQuestion.text}}
				</p>
				<p class="quiz-instruction" v-if="game.instruction && !showCurrentCorrection">
					{{game.instruction}}
				</p>
				<p class="quiz-result" v-if="showCurrentCorrection">
					Résultats
				</p>
			</div>

			<div class="quiz-simple-answer text-left text-sm mb-4" v-for="(answer, index) in currentQuestion.answers" :key="answer.id">

				<label class="quiz-simple-answer-content flex flex-row rounded-md overflow-hidden cursor-pointer">
					<div class="bg-blue w-8 text-white text-center flex-shrink-0 flex flex-row items-center justify-center">
						<span>{{getAnswerIndexLabel(index)}}</span>
					</div>

					<span class="quiz-simple-answer-text flex-grow ml-4 py-3">
						{{answer.text}}
					</span>

					<span class="quiz-simple-answer-checkbox mx-4 py-3"
					:class="{
						'right-answer': showCurrentCorrection && answer.is_correct,
						'wrong-answer': showCurrentCorrection && !answer.is_correct
					}">
						<input
						v-if="hasMultipleValidAnswers"
						type="checkbox" :name="'answer-' + index" value="1"
						v-model="currentAnswers[index]" :disabled="showCurrentCorrection">
						<input
						v-else
						type="radio" name="answer" :value="index"
						v-model="currentAnswers[0]" :disabled="showCurrentCorrection">
					</span>
				</label>
			</div>
		</div>

		<div class="quiz-simple-correction rounded-md py-3 px-4 bg-gray-lighter text-left" v-show="showCurrentCorrection && currentQuestion.correction"
		:class="{
			'bg-blue-light': isCorrect(),
		}">
			<div class="uppercase font-black">
				Réponse :
			</div>
			<div>{{currentQuestion.correction}}</div>
		</div>

		<button
		class="faa-button-blue mt-8"
		type="button"
		v-if="!showCorrection"
		@click.prevent="nextQuestion">
			{{ showCurrentCorrection ? 'Suivant' : 'Valider' }}
		</button>
	</div>
</template>

<script>
	import soundEffectMixin from '@/mixins/soundEffectMixin'

	export default {
		name: 'QuizSimple',
		mixins: [soundEffectMixin],
		props: {
			game: {
				type: Object,
				required: false,
				default: null
			},
			showCorrection: {
				type: Boolean,
				required: false,
				default: false
			}
		},
		data() {
			return {
				results: {},
				currentQuestionIndex: 0,
				currentAnswers: {},
				showCurrentCorrection: false,
				soundEffects: {
					valid: require('../../../../assets/sounds/game_validate.mp3'),
					skip: require('../../../../assets/sounds/game_continue.mp3'),
				},
			}
		},
		computed: {
			currentQuestion() {
				const questionsCount = (this.game && this.game.questions ? this.game.questions.length : 0)

				if (this.currentQuestionIndex < 0 || this.currentQuestionIndex >= questionsCount) {
					return null
				}

				return this.game.questions[this.currentQuestionIndex]
			},
			hasMultipleValidAnswers() {
				const correctAnswers = this.currentQuestion.answers.filter((answer) => {
					return answer.is_correct
				})

				return (correctAnswers.length > 1)
			}
		},
		watch: {
			game: {
				handler(game) {
					// Reset states
					this.results = {
						right: 0,
						wrong: 0,
						total: (game && game.questions && game.questions.length ? game.questions.length : 0),
					}
					this.currentQuestionIndex = 0
					this.currentAnswers = {}
					this.showCurrentCorrection = false
				},
				immediate: true,
			},
		},
		methods: {
			getAnswerIndexLabel(index) {
				let label = ''

				const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
				for (var i = 0; i < Math.ceil((index + 1) / letters.length); i++) {
					label += letters[index % letters.length]
				}

				return label
			},
			nextQuestion() {
				const newIndex = (this.currentQuestionIndex + 1)
				const questionsCount = (this.game && this.game.questions ? this.game.questions.length : 0)
				const outOfBounds = (newIndex < 0 || newIndex >= questionsCount)

				// End game after last question and show correction
				if (outOfBounds) {
					this.$emit('ended')
				} else {
					this.playSoundEffect('valid')
				}

				if (!this.showCurrentCorrection) {
					// Update results
					if (this.isCorrect()) {
						this.results.right += 1 
					} else {
						this.results.wrong += 1
					}

					this.showCurrentCorrection = true
				} else if (!outOfBounds) {
					this.playSoundEffect('skip')
					this.currentQuestionIndex = newIndex
					this.showCurrentCorrection = false
					this.currentAnswers = {}
				}
			},
			isCorrect() {
				if (this.hasMultipleValidAnswers) {
					// Check if one correct answer is not checked
					for (var i = 0; i < this.currentQuestion.answers.length; i++) {
						if (this.currentQuestion.answers[i].is_correct && !this.currentAnswers[i]) {
							return false
						}
					}

					return true
				} else {
					// Check if the selected answer is correct
					if (this.currentAnswers[0]) {
						if (this.currentQuestion.answers[this.currentAnswers[0]].is_correct) {
							return true
						}
					}
				}

				return false
			},
			getResults() {
				return this.results
			},
		},
	}
</script>

<style lang="scss" scoped>

input  {
	display: block;
	cursor: pointer;
}

input  {
	@apply appearance-none rounded border border-gray;
	width: 16px;
	height: 16px;
	margin: auto;
	
	&:checked {
		@apply border-blue;
		background: url("../../../../assets/images/check-symbol-blue.svg") no-repeat center;
		background-size: 8px 8px;
	}
}

.quiz-simple-answer-content {
	box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.15);
}

.right-answer {
	input {
		@apply bg-green border-green;
	}

	input:checked {
		@apply bg-green border-green;
		background-image: url("../../../../assets/images/check-symbol-white.svg");
		background-repeat: no-repeat;
		background-position: center;
		background-size: 8px 8px;
	}
}

.wrong-answer {
	input:checked {
		@apply bg-red border-red;
		background-image: url("../../../../assets/images/check-symbol-white.svg");
		background-repeat: no-repeat;
		background-position: center;
		background-size: 8px 8px;
	}
}

</style>
